import { Page, f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PrepaidSettings } from './PrepaidSettings/PrepaidSettings';
import { AuthInfoState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { getAndSetCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { usePaymentList } from '@/hooks/api/payment/usePaymentList';
import { usePrepaidSetting } from '@/hooks/api/prepaid/usePrepaidSetting';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import {
  BillInformation,
  Customer,
  PaymentMethod,
  Prepaid,
} from '@/types/api/customerApi';
import { paymentStorage } from '@/utils/paymentStorage';
import { storeDispatch } from '@/utils/store';

type PrepaidSettingsPageProps = {
  f7router: Router.Router;
  status?: string;
};

export const PrepaidSettingsPage: FC<PrepaidSettingsPageProps> = ({
  f7router,
  status,
}) => {
  const customerInfo = useStore(store, 'customerInfo') as Required<Customer>;
  const settingsPrevPage = store?.state.settingsPrevPage;
  const prevPage = settingsPrevPage || paymentStorage.getItem('prevPage');
  const prepaidInfo = useStore(
    store,
    'getFirstAdvancePaymentsInfo',
  ) as Required<Prepaid>;
  const billsInfo = useStore(
    store,
    'getBillInfo',
  ) as Required<BillInformation>[];
  const paymentMethods = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as Required<PaymentMethod>[];
  const [isUpdating, setIsUpdating] = useState(false);
  const { isFetched, isFetching } = usePaymentList(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const authInfo = store?.state?.authInfo ?? ({} as AuthInfoState);
  const getStormCustomerId = authInfo?.m_customer_id ?? 0;
  const { bill, payment, isLoading } = usePrepaidSetting(
    customerInfo,
    billsInfo,
    prepaidInfo,
    paymentMethods,
  );
  const customerId = paymentStorage.getItem('m_customer_id');

  const token =
    paymentStorage.getItem('token') === null
      ? undefined
      : paymentStorage.getItem('token');

  const { isLoggedIn, resetIsLoggedIn } = useLoadCustomer(
    f7.views.main.router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
  );
  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  // GMOセッション再利用検出（同期）: 初回レンダリングで間違ったページの描画を防ぐ。
  // useEffectはレンダリング後に実行されるため、useStateの初期化子で同期的に判定し、
  // リダイレクト中はnullを返して画面フラッシュを防止する。
  const [isGmoRedirecting] = useState(() => {
    if (!status) return false;
    const expectedReturn = paymentStorage.getItem('gmo_expected_return');
    const expectedCancel = paymentStorage.getItem('gmo_expected_cancel');
    if (
      status === 'gmo_success' &&
      expectedReturn &&
      !expectedReturn.includes('/prepaid_settings/')
    )
      return true;
    if (
      status === 'gmo_cancel' &&
      expectedCancel &&
      !expectedCancel.includes('/prepaid_settings/')
    )
      return true;
    return false;
  });

  // charge_prepaidからのGMOカード登録後の検出（同期）:
  // PrepaidSettings画面のフラッシュを防止するため、初回レンダリングで判定する。
  const [isChargeRedirecting] = useState(() => {
    if (status !== 'gmo_cancel' && status !== 'gmo_success') return false;
    const prev =
      store?.state.settingsPrevPage || paymentStorage.getItem('prevPage');
    return prev === 'charge_prepaid';
  });

  // GMOセッション再利用検出: GMOが前回セッションのURLにリダイレクトした場合、
  // 期待する正しいページに転送する
  useEffect(() => {
    // AccountRegistPaymentPageからのリダイレクト後、退避したfromPageを復元する
    const redirectedFromPage = paymentStorage.getItem('gmo_redirect_fromPage');
    if (redirectedFromPage) {
      paymentStorage.setItem('fromPage', redirectedFromPage);
      paymentStorage.removeItem('gmo_redirect_fromPage');
    }

    if (!status) return;
    const expectedReturn = paymentStorage.getItem('gmo_expected_return');
    const expectedCancel = paymentStorage.getItem('gmo_expected_cancel');

    const redirectToCorrectPage = (targetUrl: string) => {
      // 後続のcleanup(afterGmoRedirect)がfromPageを消す前に退避キーに保存。
      // window.location.replace()はJSの実行を即座に止めないため、
      // 他のuseEffectが走ってfromPageを削除してしまう。
      const fromPage = paymentStorage.getItem('fromPage');
      if (fromPage) {
        paymentStorage.setItem('gmo_redirect_fromPage', fromPage);
      }
      paymentStorage.removeItem('gmo_expected_return');
      paymentStorage.removeItem('gmo_expected_cancel');
      window.location.replace(targetUrl);
    };

    if (
      status === 'gmo_success' &&
      expectedReturn &&
      !expectedReturn.includes('/prepaid_settings/')
    ) {
      redirectToCorrectPage(expectedReturn);
      return;
    }
    if (
      status === 'gmo_cancel' &&
      expectedCancel &&
      !expectedCancel.includes('/prepaid_settings/')
    ) {
      redirectToCorrectPage(expectedCancel);
      return;
    }
    // 正しいページに到着した場合、期待URLをクリーンアップ
    paymentStorage.removeItem('gmo_expected_return');
    paymentStorage.removeItem('gmo_expected_cancel');
  }, []);

  // charge_prepaidからGMO経由でキャンセルして戻った場合に使用するref。
  // 早期リダイレクトeffectとafterGmoRedirectの両方で参照するため、先に宣言する。
  const redirectedRef = useRef(false);

  // charge_prepaidからGMOカード登録キャンセルで戻った場合、
  // データ読み込みを待たずにすぐcharge_prepaidに遷移する。
  // afterGmoRedirectより先に宣言し、同一レンダーサイクル内で先に実行させる。
  useEffect(() => {
    if (redirectedRef.current) return;
    if (status !== 'gmo_cancel') return;
    if (prevPage !== 'charge_prepaid') return;
    if (!isLoggedIn) return;

    redirectedRef.current = true;
    setCurrentStatus(undefined);
    // GMO関連のlocalStorageをクリーンアップ
    paymentStorage.removeItem('prevPage');
    paymentStorage.removeItem('fromPage');
    paymentStorage.removeItem('destination_page');
    paymentStorage.removeItem('destination_step');
    paymentStorage.removeItem('source_page');
    paymentStorage.removeItem('token');
    paymentStorage.removeItem('m_customer_id');
    paymentStorage.setItem('postGmoNav', 'true');
    storeDispatch('setMenu', 'menu', 'prepaid');
    f7.view.main.router.navigate(paths.chargePrepaid, { animate: false });
    // navigate()後のF7履歴から古いエントリを全削除して
    // バック時にPrepaidSettingsに戻らないようにする。
    setTimeout(() => {
      const history = f7.view.main.router.history;
      if (history.length > 1) {
        history.splice(0, history.length - 1);
      }
      window.localStorage.setItem(
        'f7router-view_main-history',
        JSON.stringify(history),
      );
    }, 0);
  }, [status, prevPage, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      handleGmoRegisterAndEditRedirect(currentStatus ?? '', isLoggedIn);
    }
  }, [isLoggedIn, currentStatus]);

  // ページを再レンダリングさせたい際に、別画面で呼び出す用の関数
  const handleIsUpdating = () => {
    setIsUpdating(true);
  };

  const afterGmoRedirect = useCallback(() => {
    // 早期リダイレクト済みの場合はクリーンアップをスキップ
    if (redirectedRef.current) return;
    if (currentStatus !== undefined && currentStatus !== '') {
      // prevPageをタブバーの有効なメニュー名にマッピング
      const tabMenu =
        prevPage === 'charge_prepaid' || prevPage === 'prepaid'
          ? 'prepaid'
          : prevPage === 'payments'
          ? 'account'
          : prevPage;
      storeDispatch('setMenu', 'menu', tabMenu);
    }

    // GMOステータスURL(/gmo_success, /gmo_cancel)をクリーンアップ
    // → 戻るボタンでループしないようにする
    const cleanupGmoUrl = () => {
      paymentStorage.removeItem('fromPage');
      paymentStorage.removeItem('destination_page');
      paymentStorage.removeItem('destination_step');
      paymentStorage.removeItem('source_page');
      paymentStorage.removeItem('token');
      paymentStorage.removeItem('m_customer_id');
      // GMOリダイレクト後はフルページリロードでF7のDOMスタックがリセットされるため、
      // handleClickBackでf7CustomBack()(=router.back())を使うとGMO URLに戻ってしまう。
      // postGmoNavフラグでnavigate()を使うよう指示する。
      paymentStorage.setItem('postGmoNav', 'true');
      window.history.replaceState({}, '', paths.prepaidSetting);
      const routerHistory = f7.view.main.router.history;
      if (routerHistory.length > 0) {
        routerHistory[routerHistory.length - 1] = paths.prepaidSetting;
      }
      window.localStorage.setItem(
        'f7router-view_main-history',
        JSON.stringify(routerHistory),
      );
    };

    switch (currentStatus) {
      case 'gmo_success':
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          resetIsLoggedIn();
          cleanupGmoUrl();
        }
        break;
      case 'gmo_cancel':
        if (!isLoggedIn) return;
        setCurrentStatus(undefined);
        resetIsLoggedIn();
        getAndSetCustomerInfo({
          m_customer_id: getStormCustomerId,
        });
        cleanupGmoUrl();
        break;
      default:
        break;
    }
  }, [currentStatus, isLoggedIn, resetIsLoggedIn]);

  // GMOからのリダイレクト処理
  useEffect(() => {
    afterGmoRedirect();
  }, [afterGmoRedirect]);

  // charge_prepaidからGMO経由で戻った場合、PrepaidSettingsを表示せず
  // charge_prepaidに自動遷移する
  // gmo_cancel: 早期リダイレクトeffectで即時遷移（データ読み込み不要）
  // gmo_success: ストアデータ（残高・決済方法）が読み込まれるまで待ってからリダイレクトする
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!(status && prevPage === 'charge_prepaid')) return;
    // ストアにデータが読み込まれるまで待つ（SPAリロード後はストアが空のため）
    if (!prepaidInfo?.id) return;

    // gmo_success: データ更新完了後に遷移
    if (status === 'gmo_success' && paymentMethods.length > 0) {
      redirectedRef.current = true;
      paymentStorage.removeItem('prevPage');
      paymentStorage.removeItem('fromPage');
      paymentStorage.setItem('postGmoNav', 'true');
      storeDispatch('setMenu', 'menu', 'prepaid');
      f7.view.main.router.navigate(paths.chargePrepaid, { animate: false });
    }
  }, [paymentMethods, prepaidInfo, prevPage, status]);

  // GMOセッション再利用リダイレクト中は何も描画しない（フラッシュ防止）
  if (isGmoRedirecting) return null;

  // charge_prepaidリダイレクト中は空のF7ページを描画。
  // F7ナビゲーションにはDOMのPage要素が必要なため、return nullではなく
  // 空のPageを返して「REVISEPay設定」タイトルのフラッシュを防止する。
  if (isChargeRedirecting) return <Page name="PrepaidSettings" />;

  return (
    <PrepaidSettings
      f7router={f7router}
      status={currentStatus}
      isLoading={isLoading}
      bill={bill}
      prepaid={prepaidInfo}
      payment={payment}
      handleIsUpdating={handleIsUpdating}
      prevPage={prevPage ?? undefined}
    />
  );
};
